import { useEffect } from 'react';
import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { scanDone, type ScanProgress } from 'shared';
import { api } from '../api';
import { toDate } from '../format';
import { tokens } from '../theme';

/**
 * Thanh tiến độ lượt quét nền — spec SC-08 §4.3.
 *
 * Thay cho dải "Đã quét N trang trong X giây" cũ. Khác biệt không chỉ ở hình
 * thức: lượt quét giờ chạy ở máy chủ và **sống qua cả việc đóng tab**, nên thanh
 * này phải hiện cả khi người dùng vừa mở màn chứ không chỉ sau khi họ bấm nút.
 *
 * Hỏi lại mỗi 2 giây trong lúc còn chạy, DỪNG hẳn khi xong. Hỏi mãi là một lượt
 * gọi mỗi 2 giây cho mọi tab đang mở, suốt cả ngày.
 */
const NHIP_MS = 2000;

/** Lượt quét xong quá lâu thì ẩn hẳn — không ai cần biết về lượt quét tuần trước. */
const AN_SAU_PHUT = 30;

function moTa(p: ScanProgress): string {
  const xong = p.done + p.failed;
  if (p.kind === 'dinh-ky') {
    return scanDone(p)
      ? `Lượt quét định kỳ hàng tuần đã xong — ${p.done}/${p.total} trang`
      : `Hệ thống đang quét định kỳ — ${xong}/${p.total} trang`;
  }
  return scanDone(p)
    ? `Đã quét xong ${p.done}/${p.total} trang`
    : `Đang quét ${xong}/${p.total} trang…`;
}

export function ScanProgressPanel({ id }: { id?: string }) {
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['quet', 'tien-do', id ?? 'gan-nhat'],
    queryFn: () => api.tienDoQuet(id),
    refetchInterval: (q) => {
      const p = q.state.data;
      return p && !scanDone(p) ? NHIP_MS : false;
    },
  });

  // Quét xong thì nạp lại bảng danh sách: điểm vừa đổi, mà không có bước này thì
  // người dùng nhìn thanh tiến độ báo "xong" trong khi bảng bên dưới vẫn là số cũ.
  const xong = data ? scanDone(data) : false;
  useEffect(() => {
    if (xong) {
      void qc.invalidateQueries({ queryKey: ['registry'] });
      void qc.invalidateQueries({ queryKey: ['alerts'] });
    }
  }, [xong, data?.id, qc]);

  if (!data) return null;
  if (data.total === 0) return null;

  // `toDate` chứ không gọi thẳng `.getTime()`: trường này là CHUỖI lúc chạy dù
  // kiểu khai là `Date` — xem ghi chú ở `format.ts`.
  const quaLau =
    data.finishedAt !== null &&
    Date.now() - toDate(data.finishedAt).getTime() > AN_SAU_PHUT * 60_000;
  if (quaLau) return null;

  const xongSo = data.done + data.failed;
  const phanTram = Math.round((xongSo / data.total) * 100);

  return (
    <Box
      sx={{
        border: `1px solid ${data.failed > 0 ? '#F3D8B7' : tokens.line}`,
        bgcolor: data.failed > 0 ? tokens.amberBg : '#fff',
        borderRadius: '10px',
        px: 2,
        py: 1.4,
        mb: 2,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{moTa(data)}</Typography>
        <Typography sx={{ fontSize: 12.5, color: tokens.muted, flex: 'none' }}>
          {phanTram}%
        </Typography>
      </Stack>

      <LinearProgress
        variant="determinate"
        value={phanTram}
        sx={{ mt: 1, height: 6, borderRadius: 3 }}
      />

      {data.failed > 0 && (
        <Typography sx={{ fontSize: 12.5, color: tokens.amberText, mt: 1 }}>
          {data.failed} trang lỗi
          {/* Nêu TÊN trang lỗi, không chỉ con số: "3 trang lỗi" không cho ai làm
              gì tiếp được. Danh sách đã cắt ở 20 mục từ phía máy chủ. */}
          {data.errors.length > 0 && `: ${data.errors.map((e) => e.slug).join(', ')}`}
        </Typography>
      )}

      {!scanDone(data) && (
        <Typography sx={{ fontSize: 12, color: tokens.faint, mt: 0.6 }}>
          Việc quét chạy ở máy chủ — đóng tab cũng không dừng nó lại.
        </Typography>
      )}
    </Box>
  );
}
